import { useCallback, useContext, useMemo } from 'react';

import { ChannelsContext, MeContext } from 'App';

import SearchServiceFiltersContext from '../components/SearchServiceFiltersContext';
import { RootContext } from './context';

export function useActiveMarket() {
  const { markets } = useContext(ChannelsContext);
  const selectedMarket = markets.selectedNode;

  return useMemo(() => {
    if (!selectedMarket) return null;

    return {
      id: selectedMarket.id,
      isoCurrencyCode: selectedMarket.currency.code,
      name: selectedMarket.name,
    };
  }, [selectedMarket?.id, selectedMarket?.currency.code, selectedMarket?.name]);
}
export type ActiveMarket = ReturnType<typeof useActiveMarket>;
export type Market = NonNullable<ActiveMarket>;

export function useActiveUploadChannelUuid() {
  const channelsContext = useContext(ChannelsContext);
  return channelsContext.channels.selectedNode?.id || null;
}
export type ActiveUploadChannelUuid = ReturnType<
  typeof useActiveUploadChannelUuid
>;

export function useFilterGroups() {
  const { filterGroups } = useContext(SearchServiceFiltersContext);
  return filterGroups || null;
}

export function useAppliedFilterGroups() {
  const { appliedFilterGroups } = useContext(SearchServiceFiltersContext);
  return appliedFilterGroups || null;
}

export function useAvailableMarkets(): Market[] {
  const { markets } = useContext(ChannelsContext);

  return useMemo(
    () =>
      markets.data.nodes.map((market) => ({
        color: market.color,
        id: market.id,
        isoCurrencyCode: market.currency.code,
        name: market.name,
      })),
    [markets.data.nodes],
  );
}

export function useChosenLinesheetRows() {
  const { state } = useContext(RootContext);
  return state.linesheet.chosenRows;
}

export function useIsAllLinesheetRowsSelected() {
  const { state } = useContext(RootContext);
  return state.linesheet.isAllRowsSelected;
}

export function useIsLinesheetStale() {
  const { state } = useContext(RootContext);
  return state.linesheet.isStale;
}

export function useIsMobileFiltersModalOpen() {
  const { state } = useContext(RootContext);
  return state.interface.isMobileFiltersModalOpen;
}

export function useIsSimulatingLoading() {
  const { state } = useContext(RootContext);
  return state.interface.isSimulatingLoading;
}

export function useIsUploadInventoryModalOpen() {
  const { state } = useContext(RootContext);
  return state.interface.isUploadInventoryModalOpen;
}

export function useLinesheetAttributes() {
  const { state } = useContext(RootContext);
  return state.linesheet.attributes;
}

export function useLinesheetEntity() {
  const { state } = useContext(RootContext);
  return state.linesheet.entity;
}

export function useLinesheetFilterKey() {
  const { state } = useContext(RootContext);
  return state.linesheet.filterKey;
}

export function useLinesheetFilterRollups() {
  const { state } = useContext(RootContext);
  return state.linesheet.filterRollups;
}

export function useLinesheetFocusedCell() {
  const { state } = useContext(RootContext);
  return useMemo(() => {
    if (state.focusedCell.hasDOMFocus) {
      return {
        column: state.focusedCell.column,
        row: state.focusedCell.row,
      };
    }

    return {
      column: null,
      row: null,
    };
  }, [
    state.focusedCell.column,
    state.focusedCell.hasDOMFocus,
    state.focusedCell.row,
  ]);
}

export function useLinesheetHiddenColumns() {
  const { state } = useContext(RootContext);
  return state.linesheet.hiddenColumns;
}

export function useLinesheetRows() {
  const { state } = useContext(RootContext);
  return state.linesheet.rows;
}

export function useLinesheetSavedMarketUuid() {
  const { state } = useContext(RootContext);
  return state.linesheet.savedMarketUuid;
}

export function useLinesheetSavedUploadChannelUuid() {
  const { state } = useContext(RootContext);
  return state.linesheet.savedUploadChannelUuid;
}

export function useLinesheetScrollToIndex() {
  const { state } = useContext(RootContext);
  return state.linesheet.scrollToIndex;
}

export function useLinesheetTotalCasepackCasesInView() {
  const { state } = useContext(RootContext);
  return state.linesheet.totalCasepackCasesInView;
}

export function useLinesheetTotalUnitsInView() {
  const { state } = useContext(RootContext);
  return state.linesheet.totalUnitsInView;
}

export function useLinesheetAvailableQuantity() {
  const { state } = useContext(RootContext);
  return state.linesheet.availableQuantity;
}

export function useLinesheetSortValues() {
  const { state } = useContext(RootContext);

  return useMemo(
    () => ({
      direction: state.linesheet.sortDirection,
      key: state.linesheet.sortKey,
      scope: state.linesheet.sortScope,
      scopeValue: state.linesheet.sortScopeValue,
    }),
    [
      state.linesheet.sortDirection,
      state.linesheet.sortKey,
      state.linesheet.sortScope,
      state.linesheet.sortScopeValue,
    ],
  );
}

export function useLinesheetViewableIndexes() {
  const { state } = useContext(RootContext);

  return useMemo(
    () => ({
      startIndex: state.linesheet.viewableStartIndex,
      stopIndex: state.linesheet.viewableStopIndex,
    }),
    [state.linesheet.viewableStartIndex, state.linesheet.viewableStopIndex],
  );
}

export function useSetActiveMarket() {
  const { markets } = useContext(ChannelsContext);

  // TODO: setSelectedNodeById changes a lot, we should fix that so this can
  // never update.
  return useCallback(
    (id: string) => {
      markets.setSelectedNodeById(id);
    },
    [markets.setSelectedNodeById],
  );
}

export function useUser() {
  const {
    me: { companyUuid, companyRole, uuid },
  } = useContext(MeContext);

  return useMemo(() => {
    if (!companyUuid || !uuid) {
      return null;
    }

    return {
      companyRole,
      companyUuid,
      uuid,
    };
  }, [companyRole, companyUuid, uuid]);
}
export type User = ReturnType<typeof useUser>;
